/* eslint-disable camelcase */
import React from 'react';
import classNames from 'classnames';
import { Video, MapPin, User, Hash, Clock } from 'react-feather';
import { routes } from 'constants/config';
import Text, { TEXT_VARIANTS } from 'components/ui/Typography/Text';
import {
  localTime,
  localTimeZone,
} from 'components/InPersonTraining/shared/LocalTimeAndTimeZone';
import DateFormat from 'constants/date_format';
import SessionDetail from './SessionDetail';

const RegistrationCapText = ({ session }) => {
  let text = t('in_person_training.event_overview.registration_cap.not_set');
  // Fix camelCase issues. Eventually when all of InPersonTraining sends proper
  // casing we can remove this.
  const maxAttendees = session.maxAttendees || session.max_attendees;
  const totalRegistered =
    session.totalRegistered || session.total_registered || 0;
  if (maxAttendees) {
    text =
      totalRegistered < maxAttendees
        ? String(maxAttendees)
        : `${String(maxAttendees).bold()} ${t(
            'in_person_training.event_overview.registration_cap.at_capacity',
          )}`;
  }

  return <span dangerouslySetInnerHTML={{ __html: text }} />;
};

const SessionDetails = ({
  session,
  className,
  eventId,
  showRegistrationCap = true,
  hasMultipleSessions = false,
  adminView,
}) => {
  // eslint-disable-next-line camelcase
  const { location, trainer } = session;

  // EventsDashboardController now properly sends camelcased keys
  // Unfortunately, everywhere else in the InPersonTraining namespace
  // incorrectly supplies React with snake_case keys. The following
  // should be removed once all endpoints send camel case keys.
  const startTime = session.startTime || session.start_time;
  const endTime = session.endTime || session.end_time;
  const timeZone = session.timeZone || session.time_zone;
  const maxAttendees = session.maxAttendees || session.max_attendees;
  const videoMeeting = session.videoMeeting || session.video_meeting;

  const videoMeetingOther = videoMeeting?.provider === 'other';
  const formatDate = (date, format) =>
    moment(date, DateFormat.fixed.ISO8601_LOCAL).format(format);
  const sessionTextVariant = hasMultipleSessions
    ? TEXT_VARIANTS.BODY_BOLD
    : TEXT_VARIANTS.BODY;
  const sessionTime = (
    <Text variant={sessionTextVariant}>
      {moment(localTime(startTime), DateFormat.fixed.ISO8601_LOCAL).format(
        DateFormat.fixed.LONG_WITH_SHORT_DAY,
      )}
      ,&nbsp;
      {formatDate(localTime(startTime), DateFormat.TIME)}&nbsp;
      {t('conjunctions.to')}&nbsp;
      {formatDate(localTime(endTime), DateFormat.TIME)}&nbsp; {' | '}{' '}
      {timeZone.formatted && (
        <Text variant={sessionTextVariant}>{localTimeZone()}</Text>
      )}
    </Text>
  );
  const eventsInProgress = videoMeetingOther;
  const meetingUrl = eventsInProgress
    ? `${document.location.origin}${routes.IN_PERSON_TRAINING.JOIN_MEETING(
        eventId,
        session.id,
      )}`
    : videoMeeting?.url;
  return (
    <>
      {hasMultipleSessions && sessionTime}
      <table
        className={classNames('EventSessionDetails', className, {
          'mt-6': hasMultipleSessions,
        })}
      >
        <tbody>
          {!hasMultipleSessions && (
            <SessionDetail
              icon={<Clock label="Time" className="EventSessionIcon" />}
              label={t(
                'in_person_training.event_overview.session_time_with_colon',
              )}
              value={sessionTime}
            />
          )}
          <SessionDetail
            icon={<User label="Instructor" className="EventSessionIcon" />}
            label={t('in_person_training.event_overview.instructor_with_colon')}
            value={trainer.name}
          />
          <SessionDetail
            icon={<MapPin label="Location" className="EventSessionIcon" />}
            label={t('in_person_training.event_overview.location_with_colon')}
            value={location}
          />
          {adminView ? (
            <SessionDetail
              icon={
                <Video
                  label="Video Conferencing"
                  className="EventSessionIcon"
                />
              }
              label={t(
                'in_person_training.event_overview.video_conference_with_colon',
              )}
              value={meetingUrl}
              isUrl
              videoMeetingOther={videoMeetingOther}
              adminView={adminView}
            />
          ) : (
            <SessionDetail
              icon={
                <Video
                  label="Video Conferencing"
                  className="EventSessionIcon"
                />
              }
              label={t(
                'in_person_training.event_overview.video_conference_with_colon',
              )}
              value={meetingUrl}
              isUrl
            />
          )}
          {showRegistrationCap && maxAttendees && (
            <SessionDetail
              icon={<Hash className="EventSessionIcon" />}
              label={t(
                'in_person_training.event_overview.registration_cap.registration_capacity',
              )}
              value={<RegistrationCapText session={session} />}
            />
          )}
        </tbody>
      </table>
    </>
  );
};

export default SessionDetails;
